import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Heart, Images, Navigation, Phone, Share2, Star } from "lucide-react";
import { toast } from "react-toastify";
import NoImage from "../assets/images/no-image-icon-23485.png";
import BackButton from "./BackButton";

interface PlaceImage {
  file_url: string;
  description?: string | null;
}

interface PlaceDetailHeroProps {
  name: string;
  rate?: number;
  reviewCount?: number;
  category?: string;
  openingHours?: string | { all_day?: string | null } | null;
  phone?: string;
  latitude?: number | null;
  longitude?: number | null;
  images: PlaceImage[];
  baseUrl?: string;
  isFavorite: boolean;
  isFavoriteLoading: boolean;
  onFavoriteClick: () => void;
  mobileNavigation?: React.ReactNode;
}

const getImageUrl = (fileUrl?: string, baseUrl?: string) => {
  if (!fileUrl) return NoImage;
  if (/^(https?:|data:|blob:)/i.test(fileUrl)) return fileUrl;
  return `${baseUrl ?? ""}${fileUrl}`;
};

const getOpeningHoursText = (openingHours: PlaceDetailHeroProps["openingHours"]) => {
  if (typeof openingHours === "string") return openingHours.trim();
  return openingHours?.all_day?.trim() || "";
};

const toEnglishDigits = (value: string) =>
  value
    .replace(/[۰-۹]/g, (digit) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String("٠١٢٣٤٥٦٧٨٩".indexOf(digit)));

const getOpenStatus = (openingHours: string): "open" | "closed" | null => {
  if (!openingHours) return null;
  if (/۲۴\s*ساعته|24\s*ساعته/.test(openingHours)) return "open";
  if (/تعطیل|بسته/.test(openingHours)) return "closed";

  const normalized = toEnglishDigits(openingHours);
  const times = Array.from(normalized.matchAll(/(?:^|\D)([01]?\d|2[0-3])(?::([0-5]\d))?(?=\D|$)/g))
    .slice(0, 2)
    .map((match) => Number(match[1]) * 60 + Number(match[2] || 0));

  if (times.length < 2) return null;

  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Tehran",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date());
  const hour = Number(parts.find((part) => part.type === "hour")?.value || 0);
  const minute = Number(parts.find((part) => part.type === "minute")?.value || 0);
  const current = hour * 60 + minute;
  const [start, end] = times;
  const isOpen = end > start
    ? current >= start && current < end
    : current >= start || current < end;

  return isOpen ? "open" : "closed";
};

const PlaceDetailHero: React.FC<PlaceDetailHeroProps> = ({
  name,
  rate,
  reviewCount,
  category,
  openingHours,
  phone,
  latitude,
  longitude,
  images,
  baseUrl,
  isFavorite,
  isFavoriteLoading,
  onFavoriteClick,
  mobileNavigation,
}) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [imageUrl, setImageUrl] = useState(() =>
    getImageUrl(images[0]?.file_url, baseUrl)
  );
  const [previousImageUrl, setPreviousImageUrl] = useState<string | null>(null);
  const touchStartX = useRef<number | null>(null);
  const transitionTimeoutRef = useRef<number | undefined>(undefined);
  const activeImage = images[activeImageIndex];
  const hasMultipleImages = images.length > 1;
  const openingHoursText = getOpeningHoursText(openingHours);
  const openStatus = getOpenStatus(openingHoursText);
  const hasDirections = latitude != null && longitude != null;
  const showPhoneAction = Boolean(phone) && !/جاذبه|attraction/i.test(category || "");

  useEffect(() => {
    setActiveImageIndex((current) => Math.min(current, Math.max(images.length - 1, 0)));
  }, [images.length]);

  useEffect(() => {
    const nextImageUrl = getImageUrl(activeImage?.file_url, baseUrl);
    if (nextImageUrl === imageUrl) return;

    setPreviousImageUrl(imageUrl);
    setIsImageLoading(true);
    setImageUrl(nextImageUrl);
  }, [activeImage?.file_url, baseUrl]);

  useEffect(() => () => window.clearTimeout(transitionTimeoutRef.current), []);

  const showPreviousImage = () => {
    if (!hasMultipleImages) return;
    setActiveImageIndex((current) => (current - 1 + images.length) % images.length);
  };

  const showNextImage = () => {
    if (!hasMultipleImages) return;
    setActiveImageIndex((current) => (current + 1) % images.length);
  };

  const handleTouchEnd = (event: React.TouchEvent<HTMLElement>) => {
    if (touchStartX.current == null || !hasMultipleImages) return;
    const distance = touchStartX.current - event.changedTouches[0].clientX;
    touchStartX.current = null;
    if (Math.abs(distance) < 40) return;
    if (distance > 0) showNextImage();
    else showPreviousImage();
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: name, url: window.location.href });
        return;
      }

      await navigator.clipboard.writeText(window.location.href);
      toast.success("لینک مکان کپی شد", {
        className: "toast",
        hideProgressBar: true,
      });
    } catch (shareError) {
      if (shareError instanceof DOMException && shareError.name === "AbortError") {
        return;
      }

      toast.error("اشتراک گذاری لینک ممکن نشد", {
        className: "toast",
        hideProgressBar: true,
      });
    }
  };

  const actionButtonClass =
    "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/40 bg-white/20 text-white shadow-md backdrop-blur-md transition-colors hover:bg-white/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black/40 disabled:cursor-not-allowed disabled:opacity-60";

  return (
    <>
      <section
        className="relative mt-[70px] h-[280px] w-full touch-pan-y overflow-hidden bg-gray-200 sm:h-[320px]"
        aria-labelledby="place-detail-title"
        onTouchStart={(event) => {
          touchStartX.current = event.touches[0].clientX;
        }}
        onTouchEnd={handleTouchEnd}
      >
        {isImageLoading && (
          <div className="absolute inset-0 animate-pulse bg-gray-200" aria-hidden="true" />
        )}

        {previousImageUrl && (
          <img
            src={previousImageUrl}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover"
          />
        )}

        <img
          key={`${activeImageIndex}-${imageUrl}`}
          src={imageUrl}
          alt={activeImage?.description || `${name}، تصویر ${activeImageIndex + 1}`}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ease-out ${isImageLoading ? "opacity-0" : "opacity-100"
            }`}
          onLoad={() => {
            setIsImageLoading(false);
            window.clearTimeout(transitionTimeoutRef.current);
            transitionTimeoutRef.current = window.setTimeout(() => setPreviousImageUrl(null), 520);
          }}
          onError={() => {
            if (imageUrl !== NoImage) {
              setImageUrl(NoImage);
            } else {
              setIsImageLoading(false);
              setPreviousImageUrl(null);
            }
          }}
        />

        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/25 via-transparent to-transparent"
          aria-hidden="true"
        />

        {hasMultipleImages && (
          <>
            <button
              type="button"
              onClick={showPreviousImage}
              aria-label="تصویر قبلی"
              className="absolute right-4 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center text-white drop-shadow-md transition hover:text-white/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <ChevronRight size={20} aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={showNextImage}
              aria-label="تصویر بعدی"
              className="absolute left-4 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center text-white drop-shadow-md transition hover:text-white/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <ChevronLeft size={20} aria-hidden="true" />
            </button>
            <span
              className="absolute bottom-4 left-4 z-10 flex items-center gap-1 rounded-lg bg-black/45 px-2 py-1 text-[10px] text-white backdrop-blur-sm font-myYekanFaNumMedium"
              aria-label={`${images.length.toLocaleString("fa-IR")} تصویر`}
            >
              {images.length.toLocaleString("fa-IR")}
              <Images size={13} strokeWidth={1.8} aria-hidden="true" />
            </span>
            <div className="absolute bottom-5 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1.5" aria-label="تصاویر مکان">
              {images.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => setActiveImageIndex(index)}
                  aria-label={`نمایش تصویر ${index + 1}`}
                  aria-current={index === activeImageIndex ? "true" : undefined}
                  className={`h-1.5 rounded-full bg-white shadow-sm transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${index === activeImageIndex ? "w-5" : "w-1.5 bg-white/70"
                    }`}
                />
              ))}
            </div>
          </>
        )}

        <div className="absolute right-4 top-4 z-20">
          <BackButton variant="overlay" reverseArrow />
        </div>

        <div className="absolute left-4 top-4 z-20 flex items-center gap-2" dir="ltr">
          <button
            type="button"
            onClick={handleShare}
            className={actionButtonClass}
            aria-label="اشتراک گذاری"
            title="اشتراک گذاری"
          >
            <Share2 size={18} strokeWidth={2} />
          </button>
          <button
            type="button"
            onClick={onFavoriteClick}
            disabled={isFavoriteLoading}
            className={actionButtonClass}
            aria-label={isFavorite ? "حذف از علاقه مندی ها" : "افزودن به علاقه مندی ها"}
            aria-pressed={isFavorite}
            title={isFavorite ? "حذف از علاقه مندی ها" : "افزودن به علاقه مندی ها"}
          >
            <Heart
              size={19}
              strokeWidth={2}
              className={isFavorite ? "fill-[#E5484D] text-[#E5484D]" : "text-white"}
            />
          </button>
        </div>

      </section>

      <div
        className="sticky top-0 z-30 border-b border-gray-200 bg-white px-4 pt-4 text-right font-myYekanFaNumRegular shadow-sm"
        dir="rtl"
      >
        <div className="flex items-center">
          <h1
            id="place-detail-title"
            className="text-[1.09rem] font-myYekanDemibold leading-[1.5] text-[#222222]"
          >
            {name}
          </h1>
        </div>

        <div className="mt-2 flex items-start justify-between gap-3">
          <div className="min-w-0">
            {typeof rate === "number" && (
              <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                <span className="text-[13px] font-myYekanFaNumDemiBold text-[#222222]" dir="ltr">
                  {Number.isInteger(rate) ? rate.toFixed(0) : rate.toFixed(1)}
                </span>
                <div className="flex items-center gap-0.5" dir="ltr" aria-label={`امتیاز ${rate} از ۵`}>
                  {Array.from({ length: 5 }, (_, index) => (
                    <Star
                      key={index}
                      size={13}
                      className={index < Math.round(rate) ? "fill-[#038545] text-[#038545]" : "fill-gray-100 text-gray-300"}
                      aria-hidden="true"
                    />
                  ))}
                </div>
                {typeof reviewCount === "number" && <span>{reviewCount} رای</span>}
              </div>
            )}

            {(category || openingHoursText) && (
              <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-2 text-xs text-gray-600">
                {category && <span className="font-myYekanMedium text-gray-700">{category}</span>}
                {category && (openStatus || openingHoursText) && <span className="h-4 w-px shrink-0 bg-gray-200" aria-hidden="true" />}
                {openStatus && (
                  <span className={`flex items-center gap-1 font-myYekanMedium ${openStatus === "open" ? "text-[#068646]" : "text-[#B42318]"}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${openStatus === "open" ? "bg-[#038545]" : "bg-[#E5484D]"}`} aria-hidden="true" />
                    {openStatus === "open" ? "باز است" : "بسته است"}
                  </span>
                )}
                {openStatus && openingHoursText && <span className="h-4 w-px shrink-0 bg-gray-200" aria-hidden="true" />}
                {openingHoursText && <span className="min-w-0 font-myYekanFaNumRegular text-gray-600"><span className="line-clamp-1">{openingHoursText}</span></span>}
              </div>
            )}
          </div>

          {(hasDirections || showPhoneAction) && (
            <div className="flex shrink-0 items-center gap-2">
              {hasDirections && (
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${longitude},${latitude}`}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`مسیریابی به ${name}`}
                  title="مسیریابی"
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border border-[#038545] text-[#038545] transition hover:bg-[#cffae7]/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#038545]"
                >
                  <Navigation size={16} aria-hidden="true" />
                </a>
              )}
              {showPhoneAction && (
                <a
                  href={`tel:${phone}`}
                  aria-label={`تماس با ${name}`}
                  title="تماس"
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border border-[#038545] text-[#038545] transition hover:bg-[#cffae7]/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#038545]"
                >
                  <Phone size={16} aria-hidden="true" />
                </a>
              )}
            </div>
          )}
        </div>

        {mobileNavigation}

        {/* {(category || openingHours) && (
          <div className="mt-3 flex flex-col gap-2 text-xs text-gray-600">
            {category && (
              <p className="w-fit rounded-full bg-[#f3fefa] px-3 py-1 font-myYekanMedium text-[#068646]">
                {category}
              </p>
            )}
            {openingHours && (
              <div className="flex items-center gap-1.5">
                <Clock3 size={16} className="shrink-0 text-[#038545]" aria-hidden="true" />
                <span>{openingHours}</span>
              </div>
            )}
          </div>
        )} */}
      </div>
    </>
  );
};

export default PlaceDetailHero;
